import React from 'react';
import {
  Box,
  Flex,
  Grid,
  Text,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Button,
  ButtonGroup,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import {
  Buildings,
  CurrencyDollar,
  Receipt,
  UsersThree,
  ArrowSquareOut,
  ArrowSquareIn,
  FileText,
  WarningCircle,
  TrendUp,
  ChartLineUp,
  Handshake,
  Prohibit,
  CheckCircle,
  Circle,
  ArrowRight,
} from 'phosphor-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from 'recharts';
import { useAuditoriaInteligente, IPeriodoParams } from '../../../../hooks/useAuditoriaInteligente';
import { fmtDataHora } from '../../../../hooks/useCooldownAtivo';
import { useAuth } from '../../../../providers/auth';
import { formatCurrency } from '../../../../utils/FormatFunctions';
import { num, pct } from '../format';
import {
  IAuditoriaEmpresa,
  Escopo,
  ICarteiraKpis,
  ICarteiraDistribuicoes,
  ICarteiraRankings,
  ICarteiraEvolucao,
  IEmpresaDashboard,
} from '../types';
import { KpiCard } from '../components/KpiCard';
import { InfoBloco, FILTRO } from '../components/InfoPopover';
import { StatusBadge } from '../components/StatusBadge';
import { EmptyState } from '../components/EmptyState';
import { UI } from '../ui';
import {
  screenForRegra,
  naturezaDe,
  splitLados,
  fmtLado,
  valorDiferenca,
  competenciaLabel,
} from './_shared/cruzamento';

interface DashboardScreenProps {
  empresa: IAuditoriaEmpresa | null;
  periodo: IPeriodoParams;
  /** Período + flag `somente_ativas` para as queries de carteira. */
  carteiraParams: IPeriodoParams & { somente_ativas?: 1 };
  /** Carteira: considerar só clientes vigentes no período. */
  somenteAtivas: boolean;
  onToggleAtivas: (v: boolean) => void;
  escopo: Escopo;
  onEscopo: (escopo: Escopo) => void;
  /** Drill: clicar numa empresa dos rankings de carteira abre o dashboard dela. */
  onOpenEmpresa: (id: number) => void;
  /** Hyperlink: clicar num KPI leva ao submódulo correspondente. */
  onScreen: (screen: string) => void;
  /** Empresa do drill ainda sendo reidratada (id na URL, objeto ainda não chegou). */
  loadingEmpresa?: boolean;
}

const REGIME_COLORS = [UI.brand, UI.pos, UI.warn, '#8B5CF6', '#1AA6B7', UI.neg];

/** Célula "Cruzamento": frase A vs B + os dois valores nomeados pelo setor + Δ. */
function AlertaCruzamento({
  regra,
  descricao,
  valorA,
  valorB,
  diferenca,
}: {
  regra: string;
  descricao: string;
  valorA: unknown;
  valorB: unknown;
  diferenca: unknown;
}) {
  const { labelA, labelB } = splitLados(descricao);
  const nat = naturezaDe(regra);
  const a = num(valorA);
  const b = num(valorB);
  const fmtA = fmtLado(regra, valorA);

  // presença/contagem/dias ou sem " vs ": não mostrar "vs R$ 0,00"
  if (nat === 'presenca' || nat === 'contagem' || nat === 'dias' || labelB === null) {
    return (
      <Box>
        <Text fontSize="12.5px" fontWeight={600} color={UI.ink}>
          {descricao}
        </Text>
        <Text fontSize="11.5px" color={UI.muted}>
          {labelA}:{' '}
          <Text as="span" fontWeight={700} color={UI.ink}>
            {fmtA}
          </Text>
          {nat === 'presenca' && (
            <Text as="span" color={UI.warn}>
              {' '}
              · ausência detectada
            </Text>
          )}
        </Text>
      </Box>
    );
  }
  // valor: A → B · Δ, cada lado nomeado pelo setor
  return (
    <Box>
      <Text fontSize="12.5px" fontWeight={600} color={UI.ink} mb={1}>
        {labelA}{' '}
        <Text as="span" color={UI.faint} fontWeight={500}>
          vs
        </Text>{' '}
        {labelB}
      </Text>
      <Flex align="center" gap={1.5} flexWrap="wrap" fontSize="11.5px" color={UI.muted}>
        <Text as="span">
          <Text as="span" color={UI.faint}>
            {labelA}:
          </Text>{' '}
          <Text as="span" fontWeight={700} color={UI.ink}>
            {formatCurrency(a)}
          </Text>
        </Text>
        <ArrowRight size={12} weight="bold" />
        <Text as="span">
          <Text as="span" color={UI.faint}>
            {labelB}:
          </Text>{' '}
          <Text as="span" fontWeight={700} color={UI.ink}>
            {formatCurrency(b)}
          </Text>
        </Text>
        <Text as="span" color={UI.faint}>
          ·
        </Text>
        <Text as="span" fontWeight={700} color={UI.neg}>
          Δ {formatCurrency(num(diferenca))}
        </Text>
      </Flex>
    </Box>
  );
}

function Panel({
  title,
  children,
  onClick,
}: {
  title: string;
  children: React.ReactNode;
  /** Torna o painel clicavel (ex.: grafico -> submodulo). */
  onClick?: () => void;
}) {
  return (
    <Box
      bg={UI.glass}
      border="1px solid"
      borderColor={UI.glassBorder}
      borderRadius="16px"
      backdropFilter={UI.glassBlur}
      boxShadow={UI.glassShadow}
      p={5}
      onClick={onClick}
      cursor={onClick ? 'pointer' : 'default'}
      transition="border-color .15s ease"
      _hover={onClick ? { borderColor: UI.brand } : undefined}
    >
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="13px" fontWeight={700} color={UI.ink}>
          {title}
        </Text>
        {onClick && <ArrowSquareOut size={14} color={UI.faint} weight="bold" />}
      </Flex>
      {children}
    </Box>
  );
}

/* ----------------------------- Carteira ----------------------------- */

function CarteiraView({
  carteiraParams,
  somenteAtivas,
  onToggleAtivas,
  onOpenEmpresa,
  onScreen,
}: {
  carteiraParams: IPeriodoParams & { somente_ativas?: 1 };
  somenteAtivas: boolean;
  onToggleAtivas: (v: boolean) => void;
  onOpenEmpresa: (id: number) => void;
  onScreen: (screen: string) => void;
}) {
  const { getCarteiraKpis, getCarteiraDistribuicoes, getCarteiraRankings, getCarteiraEvolucao } =
    useAuditoriaInteligente();

  // staleTime 5min: carteira muda devagar; evita refetch a cada navegação e
  // compartilha o cache com o Hub (mesmas queryKeys, incl. o flag somente_ativas).
  const STALE = 5 * 60 * 1000;
  const { data: kpis, isFetching: loadingKpis } = useQuery<ICarteiraKpis>({
    queryKey: ['ai-carteira-kpis', carteiraParams],
    queryFn: getCarteiraKpis,
    staleTime: STALE,
  });
  const { data: dist } = useQuery<ICarteiraDistribuicoes>({
    queryKey: ['ai-carteira-distribuicoes', carteiraParams],
    queryFn: getCarteiraDistribuicoes,
    staleTime: STALE,
  });
  const { data: rankings } = useQuery<ICarteiraRankings>({
    queryKey: ['ai-carteira-rankings', carteiraParams],
    queryFn: getCarteiraRankings,
    staleTime: STALE,
  });
  const { data: evolucao } = useQuery<ICarteiraEvolucao[]>({
    queryKey: ['ai-carteira-evolucao', carteiraParams],
    queryFn: getCarteiraEvolucao,
    staleTime: STALE,
  });

  // Backend manda faturamento como string (decimal); recharts precisa de número
  // ou o Pie/Bar não desenha (some as fatias, sobra só a legenda).
  const regimePie = (dist?.por_regime ?? []).map(r => ({
    name: r.regime,
    value: num(r.faturamento),
  }));
  const ufBar = (dist?.por_uf ?? [])
    .map(u => ({ ...u, faturamento: num(u.faturamento) }))
    .sort((a, b) => b.faturamento - a.faturamento)
    .slice(0, 8);
  const unidadeBar = (dist?.por_unidade ?? [])
    .map(u => ({ ...u, faturamento: num(u.faturamento) }))
    .sort((a, b) => b.faturamento - a.faturamento)
    .slice(0, 8);
  const evolucaoNum = (evolucao ?? []).map(e => ({
    ...e,
    faturamento: num(e.faturamento),
    impostos: num(e.impostos),
  }));

  return (
    <Box>
      <Flex justify="flex-end" align="center" gap={3} mb={3}>
        {kpis?.gerado_em && (
          <Text
            fontSize="11px"
            color={UI.muted}
            whiteSpace="nowrap"
            title="Agregados servidos de snapshot — atualize pelo botão do Hub (cooldown de 5h)"
          >
            Dados de {fmtDataHora(kpis.gerado_em)} · atualize no Hub
          </Text>
        )}
        <Button
          size="sm"
          variant="unstyled"
          display="inline-flex"
          alignItems="center"
          gap={2}
          px={3}
          h="32px"
          borderRadius="full"
          border="1px solid"
          borderColor={somenteAtivas ? UI.brand : 'rgba(15,23,42,0.12)'}
          bg={somenteAtivas ? `${UI.brand}14` : 'white'}
          color={somenteAtivas ? UI.brand : UI.muted}
          fontWeight={700}
          fontSize="12.5px"
          onClick={() => onToggleAtivas(!somenteAtivas)}
          title="Considerar apenas clientes ativos (exclui Inativa / ForaDaDominio / ForaDoAcessorias)"
        >
          {somenteAtivas ? <CheckCircle size={16} weight="fill" /> : <Circle size={16} />}
          Só clientes ativos
        </Button>
      </Flex>
      <Grid
        templateColumns={{ base: '1fr 1fr', md: 'repeat(3, 1fr)', xl: 'repeat(5, 1fr)' }}
        gap={4}
        mb={5}
      >
        <KpiCard
          label="Empresas"
          value={kpis?.total_empresas ?? 0}
          sub={kpis ? `${kpis.empresas_ativas_periodo} ativas no período` : undefined}
          icon={Buildings}
          iconColor={UI.brand}
          iconBg={UI.infoSoft}
          isLoading={loadingKpis}
          info={
            <InfoBloco
              oque="Total de empresas da carteira. O subtítulo mostra quantas estavam com contrato vigente dentro do período (dt_cli_desde/ate)."
              dados="tb_empresa (ativa, dt_cli_desde, dt_cli_ate, exclusão lógica)"
              filtro={FILTRO.carteira}
            />
          }
        />
        <KpiCard
          label="Faturamento"
          value={formatCurrency(kpis?.faturamento ?? 0)}
          onClick={() => onScreen('notas')}
          icon={CurrencyDollar}
          iconColor={UI.pos}
          iconBg={UI.posSoft}
          isLoading={loadingKpis}
          info={
            <InfoBloco
              oque="Faturamento da carteira na regra do Relatório de Faturamento do Domínio: acumuladores com IFAT = S (saídas − devoluções + serviços). Clique para abrir as Notas Fiscais."
              dados="extract_acumuladores.saldo (ifat = 'S', mensal) — mês sem extração v2 usa as notas de venda + NFS-e"
              filtro={FILTRO.carteira}
            />
          }
        />
        <KpiCard
          label="Impostos"
          value={formatCurrency(kpis?.impostos ?? 0)}
          onClick={() => onScreen('impostos-apurados')}
          icon={Receipt}
          iconColor={UI.warn}
          iconBg={UI.warnSoft}
          isLoading={loadingKpis}
          info={
            <InfoBloco
              oque="Total de impostos apurados da carteira no período (fonte de apuração). Clique para ver Impostos Apurados."
              dados="extract_impostos.valor_total (por competência)"
              filtro={FILTRO.carteira}
            />
          }
        />
        <KpiCard
          label="Com folha"
          value={kpis?.empresas_com_folha ?? 0}
          onClick={() => onScreen('pessoal')}
          icon={UsersThree}
          iconColor="#8B5CF6"
          iconBg="#F0EAFB"
          isLoading={loadingKpis}
          info={
            <InfoBloco
              oque="Quantas empresas têm folha de pagamento com proventos lançados no período. Clique para abrir Pessoal."
              dados="extract_resumo_folha (proventos > 0, distintas por empresa)"
              filtro={FILTRO.carteira}
            />
          }
        />
        <KpiCard
          label="Divergências"
          value={kpis?.divergencias ?? 0}
          onClick={() => onScreen('auditorias')}
          icon={WarningCircle}
          iconColor={UI.neg}
          iconBg={UI.negSoft}
          isLoading={loadingKpis}
          info={
            <InfoBloco
              oque="Apontamentos com status DIVERGÊNCIA do motor de auditoria na carteira. Clique para abrir as Auditorias."
              dados="Motor de regras (todas_regras) sobre extract_* fiscal/contábil/folha"
              filtro={FILTRO.carteira}
            />
          }
        />
      </Grid>

      <Grid
        templateColumns={{ base: '1fr 1fr', md: 'repeat(3, 1fr)', xl: 'repeat(5, 1fr)' }}
        gap={4}
        mb={5}
      >
        <KpiCard
          label="Notas de venda"
          value={kpis?.notas_saida ?? 0}
          onClick={() => onScreen('notas')}
          icon={ArrowSquareOut}
          iconColor={UI.brand}
          iconBg={UI.infoSoft}
          isLoading={loadingKpis}
          info={
            <InfoBloco
              oque="Quantidade de notas de venda (mercadoria) da carteira no período. Clique para abrir as Notas Fiscais."
              dados="extract_acumuladores.qtd_saidas (ifat = 'S') — mês sem extração v2 conta as notas de venda"
              filtro={FILTRO.carteira}
            />
          }
        />
        <KpiCard
          label="Notas de entrada"
          value={kpis?.notas_entrada ?? 0}
          onClick={() => onScreen('notas')}
          icon={ArrowSquareIn}
          iconColor={UI.warn}
          iconBg={UI.warnSoft}
          isLoading={loadingKpis}
          info={
            <InfoBloco
              oque="Quantidade de notas fiscais de entrada (compras) recebidas pela carteira no período. Clique para abrir as Notas Fiscais."
              dados="extract_notas_entrada (data_entrada no período)"
              filtro={FILTRO.carteira}
            />
          }
        />
        <KpiCard
          label="Notas de serviço"
          value={kpis?.notas_servico ?? 0}
          onClick={() => onScreen('notas')}
          icon={FileText}
          iconColor="#8B5CF6"
          iconBg="#F0EAFB"
          isLoading={loadingKpis}
          info={
            <InfoBloco
              oque="Quantidade de notas fiscais de serviço (NFS-e) da carteira no período. Clique para abrir as Notas Fiscais."
              dados="extract_notas_servico (data_servico no período)"
              filtro={FILTRO.carteira}
            />
          }
        />
        <KpiCard
          label="Total de notas"
          value={
            kpis
              ? Number(kpis.notas_saida) + Number(kpis.notas_entrada) + Number(kpis.notas_servico)
              : 0
          }
          onClick={() => onScreen('notas')}
          icon={TrendUp}
          iconColor={UI.brand}
          iconBg={UI.infoSoft}
          isLoading={loadingKpis}
          info={
            <InfoBloco
              oque="Soma das notas de saída, entrada e serviço da carteira no período. Clique para abrir as Notas Fiscais."
              dados="extract_notas_saida + extract_notas_entrada + extract_notas_servico (contagens no período)"
              filtro={FILTRO.carteira}
            />
          }
        />
        <KpiCard
          label="Score médio"
          value={kpis ? `${Math.round(Number(kpis.score_medio))}/100` : '—'}
          onClick={() => onScreen('auditorias')}
          icon={ChartLineUp}
          iconColor="#1AA6B7"
          iconBg="#E2F4F6"
          isLoading={loadingKpis}
          info={
            <InfoBloco
              oque="Média (por empresa) do percentual de verificações com status OK no motor de auditoria. Clique para abrir as Auditorias."
              dados="Motor de regras (todas_regras): % de status OK por empresa, depois média da carteira"
              filtro={FILTRO.carteira}
            />
          }
        />
        <KpiCard
          label="Contratos iniciados"
          value={kpis?.contratos_iniciados ?? 0}
          sub="novos clientes no período"
          icon={Handshake}
          iconColor={UI.pos}
          iconBg={UI.posSoft}
          isLoading={loadingKpis}
          info={
            <InfoBloco
              oque="Quantos clientes tiveram início de contrato dentro do período selecionado (novas entradas na carteira)."
              dados="tb_empresa.dt_cli_desde dentro do período (não excluídas)"
              filtro={FILTRO.carteira}
            />
          }
        />
        <KpiCard
          label="Contratos finalizados"
          value={kpis?.contratos_finalizados ?? 0}
          sub="encerrados no período"
          icon={Prohibit}
          iconColor={UI.neg}
          iconBg={UI.negSoft}
          isLoading={loadingKpis}
          info={
            <InfoBloco
              oque="Quantos clientes tiveram encerramento de contrato dentro do período selecionado (saídas da carteira)."
              dados="tb_empresa.dt_cli_ate dentro do período (não excluídas)"
              filtro={FILTRO.carteira}
            />
          }
        />
      </Grid>

      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={5} mb={5}>
        <Panel
          title="Evolução — faturamento × impostos"
          onClick={() => onScreen('impostos-apurados')}
        >
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={evolucaoNum}>
              <defs>
                <linearGradient id="aiFat" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={UI.brand} stopOpacity={0.22} />
                  <stop offset="100%" stopColor={UI.brand} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="aiImp" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={UI.warn} stopOpacity={0.22} />
                  <stop offset="100%" stopColor={UI.warn} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={UI.border} />
              <XAxis dataKey="mes" fontSize={11} stroke={UI.muted} />
              <YAxis
                fontSize={11}
                stroke={UI.muted}
                width={70}
                tickFormatter={v => formatCurrency(v)}
              />
              <Tooltip formatter={(v: number) => formatCurrency(v)} />
              <Legend />
              <Area
                type="monotone"
                dataKey="faturamento"
                name="Faturamento"
                stroke={UI.brand}
                fill="url(#aiFat)"
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="impostos"
                name="Impostos"
                stroke={UI.warn}
                fill="url(#aiImp)"
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>

        <Panel
          title="Faturamento por regime tributário"
          onClick={() => onScreen('impostos-por-nota')}
        >
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie
                data={regimePie}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={90}
                paddingAngle={2}
              >
                {regimePie.map((entry, i) => (
                  <Cell key={entry.name} fill={REGIME_COLORS[i % REGIME_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v: number) => formatCurrency(v)} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Panel>
      </Grid>

      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={5} mb={5}>
        <Panel title="Faturamento por UF (Top 8)">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={ufBar}>
              <CartesianGrid strokeDasharray="3 3" stroke={UI.border} />
              <XAxis dataKey="uf" fontSize={11} stroke={UI.muted} />
              <YAxis
                fontSize={11}
                stroke={UI.muted}
                width={70}
                tickFormatter={v => formatCurrency(v)}
              />
              <Tooltip formatter={(v: number) => formatCurrency(v)} />
              <Bar dataKey="faturamento" name="Faturamento" fill={UI.brand} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Top empresas por faturamento">
          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th>Empresa</Th>
                  <Th>UF</Th>
                  <Th isNumeric>Faturamento</Th>
                </Tr>
              </Thead>
              <Tbody>
                {(rankings?.top_empresas ?? []).slice(0, 8).map(e => (
                  <Tr
                    key={e.id}
                    cursor="pointer"
                    _hover={{ bg: UI.rowHover }}
                    onClick={() => onOpenEmpresa(e.id)}
                    title="Abrir o dashboard desta empresa"
                  >
                    <Td maxW="220px" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                      <Text fontWeight={700} fontSize="12.5px">
                        {e.razao_social || e.nome}
                      </Text>
                      <Text fontSize="11px" color={UI.muted}>
                        ID {e.external_id} · {e.regime}
                      </Text>
                    </Td>
                    <Td>{e.uf}</Td>
                    <Td isNumeric fontWeight={700}>
                      {formatCurrency(e.faturamento)}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        </Panel>
      </Grid>

      {unidadeBar.length > 0 && (
        <Box mb={5}>
          <Panel title="Faturamento por unidade (Top 8)">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={unidadeBar}>
                <CartesianGrid strokeDasharray="3 3" stroke={UI.border} />
                <XAxis dataKey="unidade" fontSize={11} stroke={UI.muted} />
                <YAxis
                  fontSize={11}
                  stroke={UI.muted}
                  width={70}
                  tickFormatter={v => formatCurrency(v)}
                />
                <Tooltip formatter={(v: number) => formatCurrency(v)} />
                <Bar
                  dataKey="faturamento"
                  name="Faturamento"
                  fill="#1AA6B7"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Panel>
        </Box>
      )}

      <Panel title="Maiores riscos">
        <TableContainer>
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Empresa</Th>
                <Th>CNPJ</Th>
                <Th isNumeric>Divergências</Th>
                <Th isNumeric>Alertas</Th>
                <Th isNumeric>Score</Th>
                <Th>Classificação</Th>
              </Tr>
            </Thead>
            <Tbody>
              {(rankings?.maiores_riscos ?? []).slice(0, 10).map(r => (
                <Tr
                  key={r.id}
                  cursor="pointer"
                  _hover={{ bg: UI.rowHover }}
                  onClick={() => onOpenEmpresa(r.id)}
                  title="Abrir o dashboard desta empresa"
                >
                  <Td maxW="220px" whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                    <Text fontWeight={700} fontSize="12.5px">
                      {r.nome}
                    </Text>
                    <Text fontSize="11px" color={UI.muted}>
                      ID {r.external_id}
                    </Text>
                  </Td>
                  <Td>{r.cnpj}</Td>
                  <Td isNumeric color={UI.neg} fontWeight={700}>
                    {r.divergencias}
                  </Td>
                  <Td isNumeric color={UI.warn} fontWeight={700}>
                    {r.alertas}
                  </Td>
                  <Td isNumeric fontWeight={700}>
                    {r.score}
                  </Td>
                  <Td>{r.classificacao}</Td>
                </Tr>
              ))}
              {(rankings?.maiores_riscos ?? []).length === 0 && (
                <Tr>
                  <Td colSpan={6} textAlign="center" py={6} color={UI.muted}>
                    Sem riscos no período.
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Panel>
    </Box>
  );
}

/* ----------------------- Empresa (individual) --------------------- */

function EmpresaView({
  empresa,
  periodo,
  onScreen,
}: {
  empresa: IAuditoriaEmpresa;
  periodo: IPeriodoParams;
  onScreen: (screen: string) => void;
}) {
  const { getEmpresaDashboard } = useAuditoriaInteligente();

  const { data, isFetching } = useQuery<IEmpresaDashboard>({
    queryKey: ['ai-empresa-dashboard', { id: empresa.id, ...periodo }],
    queryFn: getEmpresaDashboard,
  });

  if (isFetching && !data) {
    return (
      <Flex justify="center" py={16}>
        <Spinner />
      </Flex>
    );
  }

  const r = data?.resumo;
  // Coerção decimal-string → número para os gráficos (senão área/barra não desenha).
  const evolucaoNum = (data?.evolucao ?? []).map(e => ({
    ...e,
    vendas: num(e.vendas),
    compras: num(e.compras),
    impostos: num(e.impostos),
  }));
  const cargaNum = (data?.carga_por_categoria ?? []).map(c => ({
    ...c,
    valor: num(c.valor),
  }));

  return (
    <Box>
      <Grid
        templateColumns={{ base: '1fr 1fr', md: 'repeat(3, 1fr)', xl: 'repeat(5, 1fr)' }}
        gap={4}
        mb={5}
      >
        <KpiCard
          label="Faturamento"
          value={formatCurrency(r?.faturamento ?? 0)}
          onClick={() => onScreen('notas')}
          icon={CurrencyDollar}
          iconColor={UI.pos}
          iconBg={UI.posSoft}
          info={
            <InfoBloco
              oque="Faturamento desta empresa na regra do Relatório de Faturamento do Domínio: acumuladores com IFAT = S (saídas − devoluções + serviços). Clique para abrir as Notas Fiscais."
              dados="extract_acumuladores.saldo (ifat = 'S', mensal) — mês sem extração v2 usa as notas de venda + NFS-e"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Entradas"
          value={formatCurrency(r?.total_entradas ?? 0)}
          onClick={() => onScreen('notas')}
          icon={ArrowSquareIn}
          iconColor={UI.brand}
          iconBg={UI.infoSoft}
          info={
            <InfoBloco
              oque="Soma do valor das notas de entrada (compras) desta empresa no período. Clique para abrir as Notas Fiscais."
              dados="extract_notas_entrada.valor (data_entrada no período)"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Impostos"
          value={formatCurrency(r?.total_impostos ?? 0)}
          onClick={() => onScreen('impostos-apurados')}
          sub={r ? `Carga ${pct(r.carga_tributaria_pct)}` : undefined}
          icon={Receipt}
          iconColor={UI.warn}
          iconBg={UI.warnSoft}
          info={
            <InfoBloco
              oque="Total de impostos apurados desta empresa no período. O subtítulo mostra a carga tributária (impostos ÷ faturamento). Clique para ver Impostos Apurados."
              dados="extract_impostos.valor_total (periodo no intervalo)"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Folha líquida"
          value={formatCurrency(r?.folha_liquida ?? 0)}
          onClick={() => onScreen('pessoal')}
          icon={UsersThree}
          iconColor="#8B5CF6"
          iconBg="#F0EAFB"
          info={
            <InfoBloco
              oque="Soma do valor líquido da folha de pagamento desta empresa no período. Clique para abrir Pessoal."
              dados="extract_resumo_folha.liquido (competencia no intervalo)"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Ticket médio"
          value={formatCurrency(r?.ticket_medio ?? 0)}
          onClick={() => onScreen('notas')}
          sub={r ? `${r.qtd_notas_saida} notas de venda` : undefined}
          icon={TrendUp}
          iconColor="#1AA6B7"
          iconBg="#E2F4F6"
          info={
            <InfoBloco
              oque="Valor médio por nota de venda (valor bruto de venda ÷ quantidade de notas de venda) desta empresa no período. Clique para abrir as Notas Fiscais."
              dados="extract_acumuladores (ifat = 'S'): valor_saidas ÷ qtd_saidas (bruto, sem descontar devolução) — fallback por notas"
              filtro={FILTRO.empresa}
            />
          }
        />
      </Grid>

      <Grid templateColumns={{ base: '1fr 1fr' }} gap={4} mb={5}>
        <KpiCard
          label="Notas manuais"
          value={r?.notas_manuais ?? 0}
          onClick={() => onScreen('manuais')}
          icon={FileText}
          iconColor={UI.warn}
          iconBg={UI.warnSoft}
          info={
            <InfoBloco
              oque="Quantidade de notas lançadas manualmente desta empresa no período (sem origem em XML/integração). Clique para abrir as Notas Manuais."
              dados="extract_notas_manual (dt_emissao no período)"
              filtro={FILTRO.empresa}
            />
          }
        />
        <KpiCard
          label="Notas sem integração"
          value={r?.notas_sem_integracao ?? 0}
          onClick={() => onScreen('integracao')}
          icon={WarningCircle}
          iconColor={UI.neg}
          iconBg={UI.negSoft}
          info={
            <InfoBloco
              oque="Quantidade de notas sem integração fiscal-contábil (valor zerado no cruzamento) desta empresa no período. Clique para abrir Integração."
              dados="extract_integracao_fiscal_contabil (valor = 0; data_nota no período)"
              filtro={FILTRO.empresa}
            />
          }
        />
      </Grid>

      <Grid templateColumns={{ base: '1fr', lg: '2fr 1fr' }} gap={5} mb={5}>
        <Panel title="Evolução — vendas × compras × impostos">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={evolucaoNum}>
              <CartesianGrid strokeDasharray="3 3" stroke={UI.border} />
              <XAxis dataKey="mes" fontSize={11} stroke={UI.muted} />
              <YAxis
                fontSize={11}
                stroke={UI.muted}
                width={70}
                tickFormatter={v => formatCurrency(v)}
              />
              <Tooltip formatter={(v: number) => formatCurrency(v)} />
              <Legend />
              <Area
                type="monotone"
                dataKey="vendas"
                name="Vendas"
                stroke={UI.brand}
                fill={UI.brand}
                fillOpacity={0.12}
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="compras"
                name="Compras"
                stroke={UI.warn}
                fill={UI.warn}
                fillOpacity={0.12}
                strokeWidth={2}
              />
              <Area
                type="monotone"
                dataKey="impostos"
                name="Impostos"
                stroke={UI.neg}
                fill={UI.neg}
                fillOpacity={0.12}
                strokeWidth={2}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Carga tributária por categoria">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={cargaNum} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke={UI.border} />
              <XAxis
                type="number"
                fontSize={11}
                stroke={UI.muted}
                tickFormatter={v => formatCurrency(v)}
              />
              <YAxis
                type="category"
                dataKey="categoria"
                fontSize={11}
                stroke={UI.muted}
                width={90}
              />
              <Tooltip formatter={(v: number) => formatCurrency(v)} />
              <Bar dataKey="valor" name="Valor" fill="#8B5CF6" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
      </Grid>

      <Panel title="Alertas do período">
        <Flex justify="space-between" align="center" mb={3} gap={2} flexWrap="wrap">
          <Flex gap={2} flexWrap="wrap">
            <Flex align="center" gap={1.5} px={2.5} py={1} borderRadius="full" bg={`${UI.neg}14`}>
              <Box w="7px" h="7px" borderRadius="full" bg={UI.neg} />
              <Text fontSize="11.5px" fontWeight={700} color={UI.ink}>
                {(data?.alertas ?? []).filter(a => a.status === 'DIVERGENCIA').length} divergências
              </Text>
            </Flex>
            <Flex align="center" gap={1.5} px={2.5} py={1} borderRadius="full" bg={`${UI.warn}14`}>
              <Box w="7px" h="7px" borderRadius="full" bg={UI.warn} />
              <Text fontSize="11.5px" fontWeight={700} color={UI.ink}>
                {(data?.alertas ?? []).filter(a => a.status === 'ALERTA').length} alertas
              </Text>
            </Flex>
          </Flex>
          <Button
            size="xs"
            variant="ghost"
            color={UI.brand}
            rightIcon={<ArrowSquareOut size={12} weight="bold" />}
            onClick={() => onScreen('auditorias')}
          >
            Auditoria completa
          </Button>
        </Flex>
        <TableContainer>
          <Table size="sm" variant="simple">
            <Thead>
              <Tr>
                <Th>Regra</Th>
                <Th>Cruzamento</Th>
                <Th>Competência</Th>
                <Th>Status</Th>
                <Th isNumeric>Diferença</Th>
              </Tr>
            </Thead>
            <Tbody>
              {(data?.alertas ?? []).map(a => {
                const destino = screenForRegra(a.regra);
                const cor = a.status === 'DIVERGENCIA' ? UI.neg : UI.warn;
                return (
                  <Tr
                    key={`${a.regra}-${a.periodo}`}
                    cursor="pointer"
                    _hover={{ bg: UI.rowHover }}
                    onClick={() => onScreen(destino)}
                    title="Abrir a tela deste cruzamento"
                  >
                    <Td
                      fontWeight={700}
                      whiteSpace="nowrap"
                      borderLeft="3px solid"
                      borderLeftColor={cor}
                    >
                      {a.regra}
                    </Td>
                    <Td maxW="440px" whiteSpace="normal">
                      <AlertaCruzamento
                        regra={a.regra}
                        descricao={a.descricao}
                        valorA={a.valor_a}
                        valorB={a.valor_b}
                        diferenca={a.diferenca}
                      />
                    </Td>
                    <Td whiteSpace="nowrap">{competenciaLabel(a.periodo)}</Td>
                    <Td>
                      <StatusBadge status={a.status} />
                    </Td>
                    <Td isNumeric fontWeight={700} color={cor}>
                      {valorDiferenca(a.regra, a.diferenca)}
                    </Td>
                  </Tr>
                );
              })}
              {(data?.alertas ?? []).length === 0 && (
                <Tr>
                  <Td colSpan={5} textAlign="center" py={8} color={UI.pos}>
                    <Flex direction="column" align="center" gap={1.5}>
                      <CheckCircle size={28} weight="fill" />
                      <Text fontSize="13px" fontWeight={600}>
                        Nenhuma divergência — empresa em conformidade no período.
                      </Text>
                    </Flex>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </Panel>
    </Box>
  );
}

/* ------------------------------ Wrapper ------------------------------ */

export function DashboardScreen({
  empresa,
  periodo,
  carteiraParams,
  somenteAtivas,
  onToggleAtivas,
  escopo,
  onEscopo,
  onOpenEmpresa,
  onScreen,
  loadingEmpresa,
}: DashboardScreenProps) {
  const { temVisaoTodosGrupos } = useAuth();
  const visaoCarteira = temVisaoTodosGrupos();

  // Sem visão de carteira, o backend retorna 403 nas rotas de carteira:
  // força o escopo "empresa" e esconde o toggle.
  const escopoEfetivo: Escopo = visaoCarteira ? escopo : 'empresa';

  const renderBody = () => {
    if (escopoEfetivo === 'carteira')
      return (
        <CarteiraView
          carteiraParams={carteiraParams}
          somenteAtivas={somenteAtivas}
          onToggleAtivas={onToggleAtivas}
          onOpenEmpresa={onOpenEmpresa}
          onScreen={onScreen}
        />
      );
    if (loadingEmpresa)
      return (
        <Flex justify="center" py={16}>
          <Spinner />
        </Flex>
      );
    if (empresa) return <EmpresaView empresa={empresa} periodo={periodo} onScreen={onScreen} />;
    return (
      <EmptyState
        title="Selecione uma empresa"
        message={
          visaoCarteira
            ? 'Escolha uma empresa no topo para ver o dashboard individual, ou volte para a visão de Carteira.'
            : 'Escolha uma empresa no topo (ID, CNPJ ou Razão Social) para ver o dashboard individual.'
        }
      />
    );
  };

  return (
    <Box>
      {visaoCarteira && (
        <Flex justify="flex-end" mb={4}>
          <ButtonGroup size="sm" isAttached variant="outline">
            <Button
              onClick={() => onEscopo('carteira')}
              bg={escopo === 'carteira' ? UI.brand : 'white'}
              color={escopo === 'carteira' ? 'white' : UI.muted}
              _hover={{ bg: escopo === 'carteira' ? UI.brand : UI.rowHover }}
            >
              Carteira
            </Button>
            <Button
              onClick={() => onEscopo('empresa')}
              bg={escopo === 'empresa' ? UI.brand : 'white'}
              color={escopo === 'empresa' ? 'white' : UI.muted}
              _hover={{ bg: escopo === 'empresa' ? UI.brand : UI.rowHover }}
            >
              Empresa atual
            </Button>
          </ButtonGroup>
        </Flex>
      )}

      {renderBody()}
    </Box>
  );
}
